import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { createSearchByObject, hasRole, padZeroLeft } from '@synergy/utils';
import urlJoin from 'url-join';

import { AppState } from '../../../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list';
import { ContactsAndOrganizationsApi } from '../../../../api/contacts-and-organizations-api';
import { ParentCustomer } from '../models/parent-supplier';
import { PoolAutocompleteListAction } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list-action';
import { PoolAutocompleteListFooter } from '../../../../../../common/components/autocomplete-list/pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../../../../../common/components/abstract/form-autocomplete-list';
import { ROUTES } from '../../../../../sidebar-menu/navigation-drawer/routes';
import { User } from '../../../../../../common/models/user';

export interface PartialParentCustomer extends BaseAutocompleteSuggestionType {
    name: string;
}

interface ParentCustomerAutocompleteListProps {
    customerId?: number;
    user: User;
}

class ParentCustomerAutocompleteListBase extends PoolFormAutocompleteList<
    ParentCustomer,
    PartialParentCustomer,
    ParentCustomerAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new ContactsAndOrganizationsApi(API_BASE_URL);

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.navigation.customers"
            url={urlJoin(BASE_URL, ROUTES.contactsAndOrganizations.customers.new)}
        />
    );

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public componentDidUpdate(prevProps: ParentCustomerAutocompleteListProps) {
        if (this.props.customerId !== prevProps.customerId) {
            this.setState({ items: [] });
        }
    }

    public getSuggestionValue = (suggestion: PartialParentCustomer) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        if (!this.props.customerId) {
            this.setState({ items: [] });
            return;
        }
        const response = await this.api.Customer.CustomerDetail.Overview.getParentCustomers(
            this.props.customerId,
            createSearchByObject({ search })
        );

        const items = response.success === true ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: ParentCustomer[]): PartialParentCustomer[] =>
        items.map(parentCustomer => {
            const editLink = urlJoin(
                BASE_URL,
                ROUTES.contactsAndOrganizations.customers.detail.replace(':id', parentCustomer.id.toString())
            );
            return {
                id: padZeroLeft(parentCustomer.id, 5),
                name: parentCustomer.name,
                city: parentCustomer.mainAddress.city,
                actions: <PoolAutocompleteListAction url={editLink} allowedRoles={this.actionRoles} />,
            };
        });

    public getDisplayValue(parentCustomer: ParentCustomer) {
        return parentCustomer.name;
    }
}

const mapStateToProps = (state: AppState) => ({
    inProgress: state.customers.requesting,
    user: state.user,
});

export const ParentCustomerAutocompleteList = connect(mapStateToProps, {})(ParentCustomerAutocompleteListBase);
